import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { ComingSoonWrapper } from "./ComingSoonWrapper";
import { PaymentVerificationWrapper } from "./PaymentVerificationWrapper";
import { MFAReminderWrapper } from "@/components/auth/MFAReminderWrapper";
import { Suspense } from "react";
import { LoadingFallback } from "./LoadingFallback";
import { lazyWithRetry } from "@/utils/lazyWithRetry";

// Lazy load all pages for smaller initial bundle
const Index = lazyWithRetry(() => import("@/pages/Index"));
const Register = lazyWithRetry(() => import("@/pages/Register"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const Login = lazyWithRetry(() => import("@/pages/Login"));
const WhatsAppSignIn = lazyWithRetry(() => import("@/pages/WhatsAppSignIn"));

// Import auth guards directly since they don't have default exports
import { AuthRedirect } from "@/components/auth/AuthRedirect";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { RoleBasedRouter } from "@/components/auth/RoleBasedRouter";
import { UserMFAGuard } from "@/components/auth/UserMFAGuard";

// Lazy load auth pages
const RegisterFan = lazyWithRetry(() => import("@/pages/RegisterFan"));
const RegisterArtist = lazyWithRetry(() => import("@/pages/RegisterArtist"));
const RegisterSponsor = lazyWithRetry(() => import("@/pages/RegisterSponsor"));
const ForgotPassword = lazyWithRetry(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("@/pages/ResetPassword"));
const AuthCallback = lazyWithRetry(() => import("@/pages/AuthCallback"));

const VerifyEmail = lazyWithRetry(() => import("@/pages/VerifyEmail"));
const VerifyAccount = lazyWithRetry(() => import("@/pages/VerifyAccount"));

// Lazy load public pages
const About = lazyWithRetry(() => import("@/pages/About"));
const BlogPreviewCounter = lazyWithRetry(() => import("@/pages/BlogPreviewCounter"));
const FAQ = lazyWithRetry(() => import("@/pages/FAQ"));
const Support = lazyWithRetry(() => import("@/pages/Support"));
const TermsOfService = lazyWithRetry(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("@/pages/PrivacyPolicy"));
const CopyrightInfo = lazyWithRetry(() => import("@/pages/CopyrightInfo"));
const AMLPolicy = lazyWithRetry(() => import("@/pages/AMLPolicy"));
const ArtistsPage = lazyWithRetry(() => import("@/pages/ArtistsPage"));
const SponsorsPage = lazyWithRetry(() => import("@/pages/SponsorsPage"));
const CommunityPage = lazyWithRetry(() => import("@/pages/CommunityPage"));
const Charts = lazyWithRetry(() => import("@/pages/Charts"));
const SongsPage = lazyWithRetry(() => import("@/pages/SongsPage"));
const PublicProfilesPage = lazyWithRetry(
  () => import("@/pages/PublicProfilesPage"),
);
const PublicProfilePage = lazyWithRetry(
  () => import("@/pages/PublicProfilePage"),
);
const TrackPage = lazyWithRetry(() => import("@/pages/TrackPage"));
const PublicAlbumPage = lazyWithRetry(() => import("@/pages/PublicAlbumPage"));
const NewsletterUnsubscribe = lazyWithRetry(
  () => import("@/pages/NewsletterUnsubscribe"),
);
const Competitions = lazyWithRetry(() => import("@/pages/Competitions"));
const CompetitionDetail = lazyWithRetry(
  () => import("@/pages/CompetitionDetail"),
);
const AdminPlaylistsPage = lazyWithRetry(
  () => import("@/pages/AdminPlaylistsPage"),
);
const AdminPlaylistDetailPage = lazyWithRetry(
  () => import("@/pages/AdminPlaylistDetailPage"),
);
const CompleteArtistRegistration = lazyWithRetry(
  () => import("@/pages/CompleteArtistRegistration"),
);
const SelectAccountType = lazyWithRetry(
  () => import("@/pages/SelectAccountType"),
);
const SupportLandingPage = lazyWithRetry(
  () => import("@/pages/SupportLandingPage"),
);
const VerifyGift = lazyWithRetry(() => import("@/pages/VerifyGift"));
const EventsPage = lazyWithRetry(() => import("@/pages/EventsPage"));
const EventDetailPage = lazyWithRetry(() => import("@/pages/EventDetailPage"));
const EventTermsPage = lazyWithRetry(() => import("@/pages/EventTermsPage"));
const PlansPage = lazyWithRetry(() => import("@/pages/PlansPage"));

// Embed pages
const TrackEmbed = lazyWithRetry(() => import("@/pages/embed/TrackEmbed"));
const ProfileEmbed = lazyWithRetry(() => import("@/pages/embed/ProfileEmbed"));
const AlbumEmbed = lazyWithRetry(() => import("@/pages/embed/AlbumEmbed"));
const PlaylistEmbed = lazyWithRetry(
  () => import("@/pages/embed/PlaylistEmbed"),
);

// Protected pages
const ArtistDashboard = lazyWithRetry(() => import("@/pages/ArtistDashboard"));
const FanDashboard = lazyWithRetry(() => import("@/pages/FanDashboard"));
const SponsorDashboard = lazyWithRetry(
  () => import("@/pages/SponsorDashboard"),
);
const AdminDashboard = lazyWithRetry(() => import("@/pages/AdminDashboard"));

const ProfilePage = lazyWithRetry(() => import("@/pages/ProfilePage"));
const PlaylistPage = lazyWithRetry(() => import("@/pages/PlaylistPage"));
const FavoritesPage = lazyWithRetry(() => import("@/pages/FavoritesPage"));
const VerificationPage = lazyWithRetry(
  () => import("@/pages/VerificationPage"),
);
const VerificationSuccess = lazyWithRetry(
  () => import("@/pages/VerificationSuccess"),
);
const TransactionsPage = lazyWithRetry(
  () => import("@/pages/TransactionsPage"),
);
const AlbumDetailPage = lazyWithRetry(() => import("@/pages/AlbumDetailPage"));
const MySupportTickets = lazyWithRetry(
  () => import("@/pages/MySupportTickets"),
);

// Redirect component for backwards compatibility (old singular path)
const PlaylistRedirect = () => {
  const { playlistId } = useParams();
  return <Navigate to={`/playlists/${playlistId}`} replace />;
};

export function MainRoutes() {
  return (
    <>
      <MFAReminderWrapper />
      <Routes>
        {/* Critical routes - no lazy loading */}
        <Route
          path="/"
          element={
            <ComingSoonWrapper>
              <Layout>
                <PaymentVerificationWrapper>
                  <Suspense fallback={<LoadingFallback />}>
                    <Index />
                  </Suspense>
                </PaymentVerificationWrapper>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="register"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Register />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        {/* Auth Routes */}
        <Route
          path="login"
          element={
            <ComingSoonWrapper>
              <Layout>
                <AuthRedirect>
                  <Suspense fallback={<LoadingFallback />}>
                    <Login />
                  </Suspense>
                </AuthRedirect>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="login/whatsapp"
          element={
            <ComingSoonWrapper>
              <Layout>
                <AuthRedirect>
                  <Suspense fallback={<LoadingFallback />}>
                    <WhatsAppSignIn />
                  </Suspense>
                </AuthRedirect>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="register/fan"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterFan />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="register/artist"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterArtist />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="register/sponsor"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <RegisterSponsor />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="forgot-password"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ForgotPassword />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="reset-password"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ResetPassword />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="verify"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <VerifyEmail />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="verify-email"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <VerifyAccount />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="auth/confirm"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthCallback />
              </Suspense>
            </Layout>
          }
        />


        <Route
          path="select-account-type"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <SelectAccountType />
              </Suspense>
            </Layout>
          }
        />

        {/* Public Routes */}
        <Route
          path="/artists"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <ArtistsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/sponsors"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <SponsorsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/playlists"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminPlaylistsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/playlists/:playlistSlug"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminPlaylistDetailPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        {/* Redirect from old singular path to plural for backwards compatibility */}
        <Route path="/playlist/:playlistId" element={<PlaylistRedirect />} />

        <Route
          path="/songs"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <SongsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/community"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <CommunityPage />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/plans"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PlansPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/about"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <About />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/blog/preview-counter"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <BlogPreviewCounter />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/faq"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <FAQ />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/support"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Support />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/my-support-tickets"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <MySupportTickets />
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/terms"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <TermsOfService />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/privacy"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PrivacyPolicy />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/copyright"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <CopyrightInfo />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/aml-policy"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AMLPolicy />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/legal/event-terms"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventTermsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/newsletter/unsubscribe"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NewsletterUnsubscribe />
            </Suspense>
          }
        />
        <Route
          path="/verify-gift/:supportId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <VerifyGift />
            </Suspense>
          }
        />

        <Route
          path="/complete-artist-registration"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <CompleteArtistRegistration />
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="/competitions"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Competitions />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/competitions/:competitionSlug"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <CompetitionDetail />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/events"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventsPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="/events/:slug/:eventId"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventDetailPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        {/* Fallback for slug-less event URLs (legacy / pending redirects) */}
        <Route
          path="/events/:eventId"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <EventDetailPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="charts"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <Charts />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="profiles"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PublicProfilesPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="profiles/:username"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PublicProfilePage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="support/:username"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SupportLandingPage />
            </Suspense>
          }
        />

        <Route
          path="tracks/:trackId"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <TrackPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="albums/:albumId"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PublicAlbumPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        <Route
          path="profiles/:username/albums/:albumId"
          element={
            <ComingSoonWrapper>
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <PublicAlbumPage />
                </Suspense>
              </Layout>
            </ComingSoonWrapper>
          }
        />

        {/* Embed Routes - No layout wrapper for clean embeds */}
        <Route
          path="embed/track/:trackId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TrackEmbed />
            </Suspense>
          }
        />

        <Route
          path="embed/profile/:username"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <ProfileEmbed />
            </Suspense>
          }
        />

        <Route
          path="embed/album/:albumId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <AlbumEmbed />
            </Suspense>
          }
        />

        <Route
          path="embed/playlist/:playlistId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PlaylistEmbed />
            </Suspense>
          }
        />

        {/* Protected Routes */}
        <Route
          path="transactions"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard allowedRoles={["sponsor", "admin"]}>
                  <UserMFAGuard enforceForRoles={["sponsor"]}>
                    <TransactionsPage />
                  </UserMFAGuard>
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="verification"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <VerificationPage />
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="verification/success"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <PaymentVerificationWrapper>
                    <VerificationSuccess />
                  </PaymentVerificationWrapper>
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="favorites"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <FavoritesPage />
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route
          path="playlist"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <PlaylistPage />
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        <Route path="dashboard">
          <Route
            index
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <RoleBasedRouter />
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="artist"
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AuthGuard allowedRoles={["artist", "admin"]}>
                    <UserMFAGuard enforceForRoles={["artist"]}>
                      <ArtistDashboard />
                    </UserMFAGuard>
                  </AuthGuard>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="artist/albums/:albumId"
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AuthGuard allowedRoles={["artist", "admin"]}>
                    <UserMFAGuard enforceForRoles={["artist"]}>
                      <AlbumDetailPage />
                    </UserMFAGuard>
                  </AuthGuard>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="fan"
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AuthGuard allowedRoles={["fan", "admin"]}>
                    <UserMFAGuard enforceForRoles={["fan"]}>
                      <FanDashboard />
                    </UserMFAGuard>
                  </AuthGuard>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="sponsor"
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AuthGuard allowedRoles={["sponsor", "admin"]}>
                    <UserMFAGuard enforceForRoles={["sponsor"]}>
                      <PaymentVerificationWrapper>
                        <SponsorDashboard />
                      </PaymentVerificationWrapper>
                    </UserMFAGuard>
                  </AuthGuard>
                </Suspense>
              </Layout>
            }
          />
          <Route
            path="admin"
            element={
              <Layout>
                <Suspense fallback={<LoadingFallback />}>
                  <AuthGuard allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </AuthGuard>
                </Suspense>
              </Layout>
            }
          />
        </Route>

        <Route
          path="profile"
          element={
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <AuthGuard>
                  <ProfilePage />
                </AuthGuard>
              </Suspense>
            </Layout>
          }
        />

        {/* 404 Route — standalone, no Navbar/Footer: an unmatched path could be
            anything, so there's no pathname to key Layout's chrome rules off. */}
        <Route
          path="*"
          element={
            <ComingSoonWrapper>
              <Suspense fallback={<LoadingFallback />}>
                <NotFound />
              </Suspense>
            </ComingSoonWrapper>
          }
        />
      </Routes>
    </>
  );
}
